/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import NewApplication from '../shared/NewApplication';
import ResourceOnlyApplication from '../shared/ResourceOnlyApplication';
import ProfileCompletionGate from '../shared/ProfileCompletionGate';
import ProjectView from '../shared/ProjectView';
import { getUserDisplayName, getUserDisplayEmail, getUserStatusDisplay, isUserDeleted } from '../../utils/userUtils';
import { displayAccountNumber } from '../../utils/formatUtils';
import EditApplicationModal from './EditApplicationModal';
import useResizableColumns from '../../hooks/useResizableColumns';
import '../../styles/UnifiedProjects.css';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import {
  FirstPage,
  LastPage,
  KeyboardArrowLeft,
  KeyboardArrowRight
} from '@mui/icons-material';
import { formatClock } from '../../utils/timeFormat';

const UnifiedProjects = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State management
  const [activeSection, setActiveSection] = useState(() => {
    const path = location.pathname.split('/').pop();
    const validSections = ['new', 'saved', 'submitted', 'revision-required', 'approved', 'in-progress', 'requires-extension', 'completed', 'rejected'];
    return validSections.includes(path) ? path : 'new';
  });
  
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewingProject, setViewingProject] = useState(null);
  const [editingApplication, setEditingApplication] = useState(null);
  const [fullViewProject, setFullViewProject] = useState(null);
  const [newFormTab, setNewFormTab] = useState(0);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [facilities, setFacilities] = useState([]);
  const [facilityFilter, setFacilityFilter] = useState('all');
  
  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Drag the dividers in the header to set column widths; the browser
  // remembers them per person.
  const tableRef = useResizableColumns('user-projects');

  // Navigation sections configuration
  const sections = [
    { key: 'new', label: 'New Application', type: 'form' },
    { key: 'saved', label: 'Saved', type: 'dialog' },
    { key: 'submitted', label: 'Submitted', type: 'dialog' },
    { key: 'revision-required', label: 'Revision Required', type: 'dialog' },
    { key: 'approved', label: 'Approved', type: 'full' },
    { key: 'in-progress', label: 'In Progress', type: 'full' },
    { key: 'requires-extension', label: 'Requires Extension', type: 'full' },
    { key: 'completed', label: 'Completed', type: 'full' },
    { key: 'rejected', label: 'Rejected', type: 'dialog' }
  ];

  // Status mapping for API calls - moved outside component to prevent recreation
  // Note: 'System Removal' projects are shown to users in the Completed section as "Deleted"
  const statusMapping = useMemo(() => ({
    'saved': ['Saved'],
    'submitted': ['Submitted'],
    'revision-required': ['Revision Required', 'Revised'],
    'approved': ['Approved'],
    'in-progress': ['In Progress'],
    'requires-extension': ['Requires Extension'], // Placeholder - logic to be defined later
    'completed': ['Completed', 'System Removal'], // System Removal shows as "Deleted" for users
    'rejected': ['Rejected']
  }), []);

  // Helper function to get display status for users (System Removal -> Deleted)
  const getDisplayStatus = (status) => {
    if (status === 'System Removal') return 'Deleted';
    return status;
  };

  // Fetch all projects for counting
  const fetchAllProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/applications/saved');
      setAllProjects(response.data);
    } catch (error) {
      console.error('Error fetching all projects:', error);
      setError('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch facilities for filter dropdown
  const fetchFacilities = useCallback(async () => {
    try {
      const response = await axios.get('/api/facilities');
      setFacilities(response.data);
    } catch (error) {
      console.error('Error fetching facilities:', error);
    }
  }, []);

  // Use useMemo for filtering - this prevents re-renders that cause input focus loss
  const filteredProjects = useMemo(() => {
    if (activeSection === 'new') return [];
    
    const statuses = statusMapping[activeSection];
    if (!statuses) return [];

    // Start with projects filtered by status
    let result = allProjects.filter(project => 
      statuses.includes(project.project_status)
    );

    // Apply text search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(project => 
        project.project_title?.toLowerCase().includes(query) ||
        project.project_id?.toLowerCase().includes(query) ||
        project.users?.some(user => 
          `${user.first_name} ${user.last_name}`.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query)
        )
      );
    }

    // Apply date range filter - projects that overlap with the selected date range
    if (startDateFilter) {
      const filterStart = new Date(startDateFilter);
      result = result.filter(project => {
        if (!project.project_end_date) return true;
        const projectEnd = new Date(project.project_end_date);
        if (isNaN(projectEnd.getTime())) return true;
        return projectEnd >= filterStart;
      });
    }
    
    if (endDateFilter) {
      const filterEnd = new Date(endDateFilter);
      result = result.filter(project => {
        if (!project.project_start_date) return true;
        const projectStart = new Date(project.project_start_date);
        if (isNaN(projectStart.getTime())) return true;
        return projectStart <= filterEnd;
      });
    }

    // Apply facility filter
    if (facilityFilter !== 'all') {
      result = result.filter(project => 
        project.facility?._id === facilityFilter
      );
    }
    
    return result;
  }, [activeSection, allProjects, statusMapping, searchQuery, startDateFilter, endDateFilter, facilityFilter]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchQuery, startDateFilter, endDateFilter, facilityFilter, activeSection]);

  useEffect(() => {
    fetchAllProjects();
    fetchFacilities();
  }, [fetchAllProjects, fetchFacilities]);

  // Handle section changes
  const handleSectionChange = (section) => {
    setActiveSection(section);
    setFullViewProject(null); // Clear full view when changing sections
    navigate(`/projects/${section}`, { replace: true });
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString();
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      'Saved': 'status-saved',
      'Submitted': 'status-submitted',
      'Approved': 'status-approved',
      'In Progress': 'status-in-progress',
      'Completed': 'status-completed',
      'Rejected': 'status-rejected',
      'Revision Required': 'status-revision',
      'Revised': 'status-revision',
      'System Removal': 'status-deleted',
      'Deleted': 'status-deleted'
    };
    return statusClasses[status] || 'status-default';
  };

  // Handle view project (for full page view)
  const handleViewProject = (project) => {
    setFullViewProject(project);
  };

  // Handle view in dialog (for dialog view). Revision-required apps open the
  // two-pane form + admin chat, matching how admins review them.
  const handleViewDialog = useCallback((project) => {
    if (!project) return;
    if (project.project_status === 'Revision Required' || project.project_status === 'Revised') {
      navigate(`/projects/revise/${project._id}`);
      return;
    }
    setViewingProject(project);
  }, [navigate]);

  // Handle edit application
  const handleEdit = (application) => {
    if (application?.project_status === 'Revision Required' || application?.project_status === 'Revised') {
      navigate(`/projects/revise/${application._id}`);
      return;
    }
    setEditingApplication(application);
  };

  // Handle edit save
  const handleEditSave = async (updatedApplication) => {
    // Update the local state immediately for quick UI feedback
    setAllProjects(prev => 
      prev.map(app => 
        app._id === updatedApplication._id ? updatedApplication : app
      )
    );
    setEditingApplication(null);
    // Also refetch to ensure data consistency
    await fetchAllProjects();
  };

  // Handle edit close
  const handleEditClose = () => {
    setEditingApplication(null);
  };

  // Pagination handlers
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate pagination values
  const startIndex = page * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, filteredProjects.length);
  const paginatedProjects = filteredProjects.slice(startIndex, endIndex);

  // Dialog View Component (for Saved/Submitted/Rejected etc.)
  const DialogView = ({ project, onClose }) => {
    if (!project) {
      return null;
    }

    const handleOverlayClick = (e) => {
      if (e.target.className === 'modal-overlay') {
        onClose();
      }
    };

    return (
      <div 
        className="modal-overlay" 
        onClick={handleOverlayClick} 
        style={{
          zIndex: 9999,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <div 
          className="modal-content" 
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            width: '95%',
            maxWidth: '1200px',
            maxHeight: '95vh',
            overflow: 'auto',
            position: 'relative',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)'
          }}
        >
          <div className="modal-header">
            <div className="header-content">
              <h2>{project.application_type === 'resource_only' ? 'Resource Only Request Details' : 'Project Application Details'}</h2>
              <span className={`status-badge ${getStatusBadgeClass(project.project_status)}`}>
                {getDisplayStatus(project.project_status)}
              </span>
            </div>
            <button className="close-button" onClick={onClose}>×</button>
          </div>

          {project.application_type === 'resource_only' ? (
            <div className="project-info">
              {/* Basic Information */}
              <div className="info-section">
                <div className="section-header">
                  <h3><i className="icon-info"></i>Request Information</h3>
                </div>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Request Title</label>
                    <p>{project.project_title || 'Not specified'}</p>
                  </div>
                  <div className="info-item">
                    <label>Request ID</label>
                    <p>{project.project_id || 'Not assigned'}</p>
                  </div>
                  <div className="info-item">
                    <label>Facility</label>
                    <p>{project.facility?.facility_name || 'Not specified'}</p>
                  </div>
                  <div className="info-item">
                    <label>Submitted</label>
                    <p>{formatDate(project.createdAt)}</p>
                  </div>
                </div>
              </div>

              {/* Requester */}
              {project.users && project.users.length > 0 && (
                <div className="info-section">
                  <div className="section-header">
                    <h3><i className="icon-team"></i>People</h3>
                  </div>
                  <div className="personnel-grid">
                    {project.users.map((member, index) => (
                      <div key={index} className={`personnel-card ${isUserDeleted(member) ? 'deleted-user' : ''}`}>
                        <div className="personnel-header">
                          <h4>{getUserDisplayName(member)}</h4>
                        </div>
                        <div className="personnel-details">
                          <div className="personnel-detail">
                            <label>Email</label>
                            <span>{getUserDisplayEmail(member)}</span>
                          </div>
                          <div className="personnel-detail">
                            <label>Department</label>
                            <span>{member.department?.department_name || member.customDepartment || 'Not specified'}</span>
                          </div>
                          {member.role?.role && (
                            <div className="personnel-detail">
                              <label>Role</label>
                              <span>{member.role.role}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Purpose */}
              {project.resource_request_purpose && (
                <div className="info-section">
                  <div className="section-header">
                    <h3><i className="icon-details"></i>Purpose</h3>
                  </div>
                  <div className="info-item full-width">
                    <p style={{ whiteSpace: 'pre-wrap' }}>{project.resource_request_purpose}</p>
                  </div>
                </div>
              )}

              {/* Requested Resources */}
              <div className="info-section">
                <div className="section-header">
                  <h3><i className="icon-details"></i>Requested Resources</h3>
                </div>
                {project.requested_resources && project.requested_resources.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                        <th style={{ textAlign: 'left', padding: '8px', fontSize: '0.85rem', color: '#555' }}>Resource</th>
                        <th style={{ textAlign: 'left', padding: '8px', fontSize: '0.85rem', color: '#555' }}>Quantity</th>
                        <th style={{ textAlign: 'left', padding: '8px', fontSize: '0.85rem', color: '#555' }}>Unit</th>
                        <th style={{ textAlign: 'left', padding: '8px', fontSize: '0.85rem', color: '#555' }}>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {project.requested_resources.map((rr, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '8px', fontSize: '0.9rem' }}>{rr.resource?.resource || rr.resource?.name || 'Unknown'}</td>
                          <td style={{ padding: '8px', fontSize: '0.9rem' }}>{rr.quantity}</td>
                          <td style={{ padding: '8px', fontSize: '0.9rem' }}>{rr.resource?.unit?.base_unit || rr.resource?.unit?.unit_short || '—'}</td>
                          <td style={{ padding: '8px', fontSize: '0.9rem', color: '#666' }}>{rr.notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p style={{ color: '#999' }}>No resources requested.</p>
                )}
              </div>

              {/* Comments for rejected / revision-required */}
              {(project.project_status === 'Rejected' || project.project_status === 'Revision Required') &&
                project.comments && project.comments.length > 0 && (
                <div className="info-section">
                  <div className="section-header">
                    <h3><i className="icon-comments"></i>Comments</h3>
                  </div>
                  <div className="comments-display">
                    {project.comments
                      .filter(comment =>
                        comment.comment_type === 'rejection' ||
                        comment.comment_type === 'revision_request' ||
                        comment.comment_type === 'general'
                      )
                      .slice(0, 3)
                      .map((comment, index) => (
                        <div key={index} className="comment-item">
                          <div className="comment-header">
                            <span className="comment-author">{comment.commenter_name}</span>
                            <span className="comment-date">{formatDate(comment.timestamp)}</span>
                          </div>
                          <div className="comment-text">{comment.comment}</div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>
          ) : (
          <div className="project-info">
            {/* Basic Information Section */}
            <div className="info-section">
              <div className="section-header">
                <h3><i className="icon-info"></i>Basic Information</h3>
              </div>
              <div className="info-grid">
                <div className="info-item">
                  <label>Project Title</label>
                  <p>{project.project_title || 'Not specified'}</p>
                </div>
                <div className="info-item">
                  <label>Proposal ID</label>
                  <p>{project.project_id || 'Not assigned'}</p>
                </div>
                <div className="info-item">
                  <label>Start Date</label>
                  <p>{formatDate(project.project_start_date)}</p>
                </div>
                <div className="info-item">
                  <label>End Date</label>
                  <p>{formatDate(project.project_end_date)}</p>
                </div>
              </div>
            </div>

            {/* Facility Information */}
            <div className="info-section">
              <div className="section-header">
                <h3><i className="icon-facility"></i>Facility Information</h3>
              </div>
              <div className="info-grid">
                <div className="info-item">
                  <label>Facility</label>
                  <p>{project.facility?.facility_name || 'Not specified'}</p>
                </div>
                <div className="info-item">
                  <label>Pot Requirements</label>
                  <div className="pot-requirements">
                    {project.pots && project.pots.length > 0 ? (
                      project.pots.map((pot, index) => (
                        <div key={index} className="pot-item">
                          <span className="pot-size">{pot.pot_size?.pot || 'Unknown Size'}</span>
                          <span className="pot-quantity">× {pot.quantity}</span>
                        </div>
                      ))
                    ) : project.number_of_pots || project.pot_size ? (
                      // Legacy format fallback
                      <div className="pot-item">
                        <span className="pot-size">{project.pot_size?.pot || 'Unknown Size'}</span>
                        <span className="pot-quantity">× {project.number_of_pots || 'Unknown'}</span>
                      </div>
                    ) : (
                      <p>Not specified</p>
                    )}
                  </div>
                </div>
                <div className="info-item">
                  <label>Number of Carts</label>
                  <p>{project.number_of_carts || 'Not specified'}</p>
                </div>
              </div>
            </div>

            {/* Team Members */}
            <div className="info-section">
              <div className="section-header">
                <h3><i className="icon-team"></i>Team Members</h3>
              </div>
              <div className="personnel-grid">
                {project.users && project.users.length > 0 ? (
                  project.users.map((member, index) => (
                    <div key={index} className={`personnel-card ${isUserDeleted(member) ? 'deleted-user' : ''}`}>
                      <div className="personnel-header">
                        <h4>{getUserDisplayName(member)}</h4>
                      </div>
                      <div className="personnel-details">
                        <div className="personnel-detail">
                          <label>Email</label>
                          <span>{getUserDisplayEmail(member)}</span>
                        </div>
                        <div className="personnel-detail">
                          <label>Department</label>
                          <span>{member.department?.department_name || member.customDepartment || 'Not specified'}</span>
                        </div>
                        <div className="personnel-detail">
                          <label>Role</label>
                          <span>{member.role?.role || member.role || 'Unknown Role'}</span>
                        </div>
                        <div className="personnel-detail">
                          <label>Status</label>
                          <span className={`status-badge status-${getUserStatusDisplay(member).toLowerCase()}`}>
                            {getUserStatusDisplay(member)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No team members specified</p>
                )}
              </div>
            </div>

            {/* Experiment Conditions */}
            {project.experiment_conditions && project.experiment_conditions.length > 0 && (
              <div className="info-section">
                <div className="section-header">
                  <h3><i className="icon-conditions"></i>Experiment Conditions</h3>
                </div>
                {project.experiment_conditions.map((condition, index) => (
                  <div key={index} className="condition-card">
                    <h4>Condition Set {index + 1}</h4>
                    <div className="info-grid">
                      <div className="info-item">
                        <label>Photo Period On</label>
                        <p>{condition.photo_on ? formatClock(condition.photo_on) : 'Not specified'}</p>
                      </div>
                      <div className="info-item">
                        <label>Photo Period Off</label>
                        <p>{condition.photo_off ? formatClock(condition.photo_off) : 'Not specified'}</p>
                      </div>
                      <div className="info-item">
                        <label>Night Interrupt On</label>
                        <p>{condition.night_interrupt_on ? formatClock(condition.night_interrupt_on) : 'Not specified'}</p>
                      </div>
                      <div className="info-item">
                        <label>Night Interrupt Off</label>
                        <p>{condition.night_interrupt_off ? formatClock(condition.night_interrupt_off) : 'Not specified'}</p>
                      </div>
                      <div className="info-item">
                        <label>Day Temperature</label>
                        <p>{condition.temp_day || 'Not specified'}</p>
                      </div>
                      <div className="info-item">
                        <label>Night Temperature</label>
                        <p>{condition.temp_night || 'Not specified'}</p>
                      </div>
                      <div className="info-item">
                        <label>Day CO2</label>
                        <p>{condition.co2_day || 'Not specified'}</p>
                      </div>
                      <div className="info-item">
                        <label>Night CO2</label>
                        <p>{condition.co2_night || 'Not specified'}</p>
                      </div>
                      <div className="info-item">
                        <label>Day Humidity</label>
                        <p>{condition.humidity_day || 'Not specified'}</p>
                      </div>
                      <div className="info-item">
                        <label>Night Humidity</label>
                        <p>{condition.humidity_night || 'Not specified'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Experiment Details */}
            {project.experiment_details && project.experiment_details.length > 0 && (
              <div className="info-section">
                <div className="section-header">
                  <h3><i className="icon-experiment"></i>Experiment Details</h3>
                </div>
                {project.experiment_details.map((detail, index) => (
                  <div key={index} className="detail-card">
                    <h4>Detail Set {index + 1}</h4>
                    <div className="info-grid">
                      <div className="info-item">
                        <label>Water Type</label>
                        <p>{detail.water?.description || 'Not specified'}</p>
                      </div>
                      <div className="info-item">
                        <label>Substrate</label>
                        <p>{detail.substrate?.name ? `${detail.substrate.name}${detail.substrate.description ? ` - ${detail.substrate.description}` : ''}` : 'Not specified'}</p>
                      </div>
                      <div className="info-item">
                        <label>Species</label>
                        <p>{detail.species?.name ? `${detail.species.name}${detail.species.scientific_name ? ` (${detail.species.scientific_name})` : ''}` : detail.species_not_listed || 'Not specified'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Safety and Compliance */}
            <div className="info-section">
              <div className="section-header">
                <h3><i className="icon-safety"></i>Safety & Compliance</h3>
              </div>
              <div className="info-grid">
                <div className="info-item">
                  <label>Uses Transgenic Plants</label>
                  <p>{project.uses_transgenic_plants ? 'Yes' : 'No'}</p>
                </div>
                <div className="info-item">
                  <label>Uses Pathogen</label>
                  <p>{project.uses_pathogen ? 'Yes' : 'No'}</p>
                </div>
                {(project.uses_transgenic_plants || project.uses_pathogen) && (
                  <div className="info-item">
                    <label>BUA Number</label>
                    <p>{project.bua_number || 'Not specified'}</p>
                  </div>
                )}
                <div className="info-item">
                  <label>Safety Training Verified</label>
                  <p>{project.safety_training_verified ? 'Yes' : 'No'}</p>
                </div>
              </div>
            </div>

            {/* Funding Information */}
            {project.funding && project.funding.length > 0 && (
              <div className="info-section">
                <div className="section-header">
                  <h3><i className="icon-funding"></i>Funding Information</h3>
                </div>
                {project.funding.map((fund, index) => (
                  <div key={index} className="funding-card">
                    <div className="info-grid">
                      <div className="info-item">
                        <label>Account Number</label>
                        <p>{displayAccountNumber(fund.account_number) || 'Not specified'}</p>
                      </div>
                      <div className="info-item">
                        <label>Source of Funds</label>
                        <p>{fund.source_of_funds || 'Not specified'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Project Details */}
            <div className="info-section">
              <div className="section-header">
                <h3><i className="icon-details"></i>Project Details</h3>
              </div>
              <div className="info-item full-width">
                <label>Project Timeline</label>
                <p>{project.project_timeline || 'Not specified'}</p>
              </div>
              <div className="info-item full-width">
                <label>Experimental Design Summary</label>
                <p>{project.experimental_design_summary || 'Not specified'}</p>
              </div>
              <div className="info-item full-width">
                <label>Special Requirements</label>
                <p>{project.special_environmental_requirements || 'Not specified'}</p>
              </div>
              <div className="info-item full-width">
                <label>Additional Comments</label>
                <p>{project.additional_comments || 'Not specified'}</p>
              </div>
            </div>

            {/* Comments (for rejected projects and revision required) */}
            {(project.project_status === 'Rejected' || project.project_status === 'Revision Required') && 
             project.comments && project.comments.length > 0 && (
              <div className="info-section">
                <div className="section-header">
                  <h3><i className="icon-comments"></i>Comments</h3>
                </div>
                <div className="comments-display">
                  {/* Show recent comments related to rejection or revision */}
                  {project.comments
                    .filter(comment => 
                      comment.comment_type === 'rejection' || 
                      comment.comment_type === 'revision_request' ||
                      comment.comment_type === 'general'
                    )
                    .slice(0, 3) // Show only the 3 most recent relevant comments
                    .map((comment, index) => (
                      <div key={index} className="comment-item">
                        <div className="comment-header">
                          <span className="comment-author">{comment.commenter_name}</span>
                          <span className="comment-date">{formatDate(comment.timestamp)}</span>
                        </div>
                        <div className="comment-text">{comment.comment}</div>
                      </div>
                    ))
                  }
                </div>
              </div>
            )}
          </div>
          )}

          <div className="modal-footer">
            {(project.project_status === 'Saved' || project.project_status === 'Revision Required' || project.project_status === 'Request Changes') && (
              <button className="btn-edit" onClick={() => handleEdit(project)}>
                Edit Application
              </button>
            )}
            <button className="btn-close" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Handle search change
  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  // Handle start date change
  const handleStartDateChange = useCallback((e) => {
    setStartDateFilter(e.target.value);
  }, []);

  // Handle end date change
  const handleEndDateChange = useCallback((e) => {
    setEndDateFilter(e.target.value);
  }, []);

  // Handle facility change
  const handleFacilityChange = useCallback((e) => {
    setFacilityFilter(e.target.value);
  }, []);


  // Render table content
  const renderTableContent = () => {
    if (loading) return <div className="loading">Loading projects...</div>;
    if (error) return <div className="error">{error}</div>;

    if (filteredProjects.length === 0) {
      return (
        <div className="no-projects">
          <p>No projects found for this status.</p>
        </div>
      );
    }

    return (
      <div className="projects-table">
        <table ref={tableRef} className="resizable-table">
          <thead>
            <tr>
              {/* Proposal ID is internal: it identifies a project in code and
                  accounting, but nobody reads it off this list, and the column
                  pushed the row past the width of a laptop screen. It is still
                  searchable below, and still shown on the project's own page. */}
              <th className="col-project-title">Project Title</th>
              <th>Type</th>
              <th className="col-facility">Facility</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedProjects.map((project) => (
              <tr key={project._id}>
                <td className="col-project-title">{project.project_title}</td>
                <td>
                  {project.application_type === 'resource_only' ? (
                    <Chip
                      label="Resource"
                      size="small"
                      sx={{ backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '0.75rem' }}
                    />
                  ) : (
                    <Chip
                      label="Project"
                      size="small"
                      sx={{ backgroundColor: '#f3e5f5', color: '#7b1fa2', fontWeight: 600, fontSize: '0.75rem' }}
                    />
                  )}
                </td>
                <td className="col-facility">{project.facility?.facility_name || 'Not specified'}</td>
                <td>{formatDate(project.project_start_date)}</td>
                <td>{formatDate(project.project_end_date)}</td>
                <td>
                  <span className={`status-badge ${getStatusBadgeClass(project.project_status)}`}>
                    {getDisplayStatus(project.project_status)}
                  </span>
                </td>
                <td className="actions-cell">
                  {sections.find(s => s.key === activeSection)?.type === 'full' ? (
                    <button 
                      className="btn-view" 
                      onClick={() => handleViewProject(project)}
                    >
                      View Project
                    </button>
                  ) : (
                    <>
                      <button 
                        className="btn-view" 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleViewDialog(project);
                        }}
                      >
                        View Details
                      </button>
                      {(project.project_status === 'Saved' || project.project_status === 'Revision Required' || project.project_status === 'Request Changes') && (
                        <button 
                          className="btn-edit" 
                          onClick={() => handleEdit(project)}
                        >
                          Edit
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <span>Show</span>
            <Select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              sx={{ mx: 1 }}
              size="small"
            >
              {[10, 25, 50, 100].map(n => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </Select>
            <span>entries</span>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton aria-label="First page" onClick={() => handleChangePage(0)} disabled={page === 0}>
              <FirstPage />
            </IconButton>
            <IconButton aria-label="Previous page" onClick={() => handleChangePage(page - 1)} disabled={page === 0}>
              <KeyboardArrowLeft />
            </IconButton>
            <Box sx={{ mx: 2 }}>
              Page {page + 1} of {Math.max(1, Math.ceil(filteredProjects.length / rowsPerPage))}
            </Box>
            <IconButton aria-label="Next page"
              onClick={() => handleChangePage(page + 1)}
              disabled={page >= Math.ceil(filteredProjects.length / rowsPerPage) - 1}
            >
              <KeyboardArrowRight />
            </IconButton>
            <IconButton aria-label="Last page"
              onClick={() => handleChangePage(Math.ceil(filteredProjects.length / rowsPerPage) - 1)}
              disabled={page >= Math.ceil(filteredProjects.length / rowsPerPage) - 1}
            >
              <LastPage />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ mt: 2, color: 'text.secondary' }}>
          Showing {filteredProjects.length > 0 ? startIndex + 1 : 0} to {endIndex} of {filteredProjects.length} entries
          {(searchQuery || startDateFilter || endDateFilter || facilityFilter !== 'all') && 
            ` (filtered from ${allProjects.filter(p => statusMapping[activeSection]?.includes(p.project_status)).length} total entries)`}
        </Box>
      </div>
    );
  };

  // Get section label
  const currentSection = sections.find(s => s.key === activeSection);

  // Determine what to show in content area
  const showFiltersAndTable = activeSection !== 'new' && !fullViewProject;

  return (
    <div className="manage-program-container">
      <div className="sidebar">
        <h2>Projects</h2>
        <ul>
          {sections.map((section) => {
            // Calculate count for each section
            let count = 0;
            if (section.key !== 'new') {
              const statuses = statusMapping[section.key];
              if (statuses) {
                count = allProjects.filter(project => 
                  statuses.includes(project.project_status)
                ).length;
              }
            }
            
            return (
              <li key={section.key}>
                <button
                  type="button"
                  onClick={() => handleSectionChange(section.key)}
                  className={activeSection === section.key ? 'active' : ''}
                  aria-current={activeSection === section.key ? 'page' : undefined}
                >
                  {section.label}{section.key !== 'new' ? ` (${count})` : ''}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      
      <div className="content-area">
        {/* Full Project View */}
        {fullViewProject && (
          <ProjectView 
            user={user}
            project={fullViewProject}
            embedded={true}
            onBack={() => setFullViewProject(null)}
          />
        )}

        {/* New Application Form with Tab Switching */}
        {activeSection === 'new' && !fullViewProject && (
          <div>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={newFormTab}
                onChange={(e, val) => setNewFormTab(val)}
                sx={{
                  // These tabs are white text on a brand-coloured fill. The fill
                  // used to come from a global `button[type="button"]` rule in
                  // NewApplication.css that painted every MUI button; that rule
                  // no longer reaches MUI, so the tabs set it themselves.
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    minHeight: 48,
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1,
                    backgroundColor: 'var(--brand-primary, #c41e3a)',
                    margin: '10px 0',
                    '&:hover': {
                      backgroundColor: 'var(--brand-primary-hover, #a01830)',
                    },
                  },
                  '& .Mui-selected': {
                    color: '#fff !important',
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#fff',
                  }
                }}
              >
                <Tab label="Project Application" />
                <Tab label="Resource Only Request" />
              </Tabs>
            </Box>
            <ProfileCompletionGate>
              {newFormTab === 0 && <NewApplication user={user} />}
              {newFormTab === 1 && <ResourceOnlyApplication user={user} />}
            </ProfileCompletionGate>
          </div>
        )}

        {/* Table View with Filters */}
        {showFiltersAndTable && (
          <div className="table-view">
            <h2>{currentSection?.label} Projects</h2>
            
            {/* Filters Section - rendered inline to maintain focus */}
            <div className="filters-section">
              <div className="search-filters">
                <TextField
                  key="search-filter"
                  label="Search"
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search by title, ID, or user..."
                  sx={{ mr: 2, minWidth: 200 }}
                />
                
                <TextField
                  key="start-date-filter"
                  label="Start Date"
                  type="date"
                  value={startDateFilter}
                  onChange={handleStartDateChange}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ mr: 2 }}
                />
                
                <TextField
                  key="end-date-filter"
                  label="End Date"
                  type="date"
                  value={endDateFilter}
                  onChange={handleEndDateChange}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ mr: 2 }}
                />
                
                <FormControl size="small" sx={{ minWidth: 150, mr: 2 }}>
                  <InputLabel>Facility</InputLabel>
                  <Select
                    key="facility-filter"
                    value={facilityFilter}
                    onChange={handleFacilityChange}
                    label="Facility"
                  >
                    <MenuItem value="all">All Facilities</MenuItem>
                    {facilities.map((facility) => (
                      <MenuItem key={facility._id} value={facility._id}>
                        {facility.facility_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>

            {renderTableContent()}
          </div>
        )}
      </div>

      {/* Dialog View Modal */}
      {viewingProject && (
        <DialogView 
          project={viewingProject} 
          onClose={() => setViewingProject(null)} 
        />
      )}

      {/* Edit Application Modal */}
      {editingApplication && (
        <EditApplicationModal
          application={editingApplication}
          user={user}
          onSave={handleEditSave}
          onClose={handleEditClose}
        />
      )}
    </div>
  );
};

export default UnifiedProjects;
